/**
 * Reveal - viewport-triggered fade-in + slide-up using IntersectionObserver.
 *
 * Used to compose cinematic scroll reveals on public-facing pages (Landing,
 * Audit, etc). Strictly NOT used inside the authenticated dashboard, where
 * the requirement is instant rendering / zero artificial latency.
 *
 * - Children render with `opacity-0 translate-y-4` initial state.
 * - Once the wrapper crosses 12% of the viewport, the .revealed class is
 *   applied and the CSS transition runs (~700ms, premium ease-out).
 * - One-shot by default (no re-trigger on scroll-back).
 * - Honours `prefers-reduced-motion: reduce` via global CSS - the
 *   transition is no-opped for those users, so content lands instantly.
 *
 * Stagger children via the `delay` prop in milliseconds. Don't go above
 * ~250ms total for the section or it starts to feel sluggish.
 */
import { useEffect, useRef, useState } from "react";

export default function Reveal({
  children,
  delay = 0,
  as: As = "div",
  className = "",
  threshold = 0.12,
  ...rest
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // Skip animation work entirely if the user prefers reduced motion.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold, rootMargin: "0px 0px -40px 0px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [threshold]);

  return (
    <As
      ref={ref}
      className={`reveal ${visible ? "reveal-on" : ""} ${className}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </As>
  );
}
